import { DEFAULT_HOSPITAL_CONFIG } from './defaultPortalHospitalConfig'

const PNG_SIG = Object.freeze([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])

function matchAt(bytes, offset, sig) {
  if (offset + sig.length > bytes.length) return false
  for (let j = 0; j < sig.length; j += 1) {
    if (bytes[offset + j] !== sig[j]) return false
  }
  return true
}

function uint8ToBase64(u8) {
  if (typeof btoa === 'undefined') return null
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < u8.length; i += chunk) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, Math.min(i + chunk, u8.length)))
  }
  return btoa(binary)
}

export function resolveEmpresaLogoPortal(raw) {
  if (raw == null || raw === '') return null
  if (typeof raw === 'string') {
    let s = raw.trim()
    if (!s) return null
    if (/^https?:\/\//i.test(s)) return s
    if (/^data:/i.test(s)) {
      if (/^data:image\//i.test(s)) {
        return s.replace(/\s/g, '')
      }
      return s.replace(/\s/g, '')
    }
    if (/^image\/[a-z0-9.+-]+;base64,/i.test(s)) {
      return `data:${s}`
    }
    if (/^<\?xml/i.test(s) || /^<svg[\s>]/i.test(s)) {
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`
    }

    s = s.replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/')
    if (!s.length) return null
    const pad = s.length % 4
    if (pad) s += '='.repeat(4 - pad)

    let bytes
    try {
      const binary = atob(s)
      bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    } catch {
      return null
    }

    const riffWebpAt0 = bytes.length >= 12
      && String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) === 'RIFF'
      && String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]) === 'WEBP'

    if (matchAt(bytes, 0, PNG_SIG)) {
      return `data:image/png;base64,${s}`
    }
    if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
      return `data:image/jpeg;base64,${s}`
    }
    if (
      bytes.length >= 6
      && bytes[0] === 0x47
      && bytes[1] === 0x49
      && bytes[2] === 0x46
      && bytes[3] === 0x38
      && (bytes[4] === 0x37 || bytes[4] === 0x39)
      && bytes[5] === 0x61
    ) {
      return `data:image/gif;base64,${s}`
    }
    if (riffWebpAt0) {
      return `data:image/webp;base64,${s}`
    }

    const scanLen = Math.min(bytes.length, 8192)
    const sliceToDataUrl = (start, mime) => {
      const slice = bytes.subarray(start)
      const b64img = uint8ToBase64(slice)
      return b64img != null ? `data:${mime};base64,${b64img}` : null
    }

    // Firmas largas primero (menos falsos positivos que FF D8 suelto)
    for (let i = 0; i <= scanLen - 8; i += 1) {
      if (matchAt(bytes, i, PNG_SIG)) {
        const u = sliceToDataUrl(i, 'image/png')
        if (u) return u
      }
    }
    for (let i = 0; i <= scanLen - 12; i += 1) {
      if (
        bytes[i] === 0x52
        && bytes[i + 1] === 0x49
        && bytes[i + 2] === 0x46
        && bytes[i + 3] === 0x46
        && bytes[i + 8] === 0x57
        && bytes[i + 9] === 0x45
        && bytes[i + 10] === 0x42
        && bytes[i + 11] === 0x50
      ) {
        const u = sliceToDataUrl(i, 'image/webp')
        if (u) return u
      }
    }
    for (let i = 0; i <= scanLen - 6; i += 1) {
      if (
        bytes[i] === 0x47
        && bytes[i + 1] === 0x49
        && bytes[i + 2] === 0x46
        && bytes[i + 3] === 0x38
        && (bytes[i + 4] === 0x37 || bytes[i + 4] === 0x39)
        && bytes[i + 5] === 0x61
      ) {
        const u = sliceToDataUrl(i, 'image/gif')
        if (u) return u
      }
    }
    for (let i = 0; i <= scanLen - 2; i += 1) {
      if (bytes[i] === 0xff && bytes[i + 1] === 0xd8) {
        const u = sliceToDataUrl(i, 'image/jpeg')
        if (u) return u
        break
      }
    }

    // BMP embebido (caso General/OLE de VFP) — como geclisa-site
    let bmpStart = -1
    for (let i = 0; i < bytes.length - 1; i += 1) {
      if (bytes[i] === 0x42 && bytes[i + 1] === 0x4d) {
        bmpStart = i
        break
      }
    }
    if (bmpStart >= 0) {
      const bmpBytes = bytes.subarray(bmpStart)
      try {
        const blob = new Blob([bmpBytes], { type: 'image/bmp' })
        return URL.createObjectURL(blob)
      } catch {
        const b64 = uint8ToBase64(bmpBytes)
        return b64 != null ? `data:image/bmp;base64,${b64}` : null
      }
    }

    const b0 = bytes[0]
    const b1 = bytes[1]
    const b2 = bytes[2]
    const b3 = bytes[3]
    let mime = 'image/png'
    if (b0 === 0xff && b1 === 0xd8) mime = 'image/jpeg'
    else if (b0 === 0x89 && b1 === 0x50 && b2 === 0x4e && b3 === 0x47) mime = 'image/png'
    else if (b0 === 0x47 && b1 === 0x49 && b2 === 0x46) mime = 'image/gif'
    else if (riffWebpAt0) mime = 'image/webp'

    return `data:${mime};base64,${s}`
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const u8 = new Uint8Array(raw)
    let binary = ''
    for (let i = 0; i < u8.length; i += 1) {
      binary += String.fromCharCode(u8[i])
    }
    if (typeof btoa === 'undefined') return null
    return resolveEmpresaLogoPortal(btoa(binary))
  }
  return null
}

function escapeHtmlText(raw) {
  return String(raw ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function textToHelpHtml(raw) {
  const text = String(raw ?? '').trim()
  if (!text) return ''
  return escapeHtmlText(text)
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '')
    .map((line) => `<p>${line}</p>`)
    .join('')
}

function normalizePortalHelpHtml(raw) {
  return String(raw ?? '')
    .replace(/^[ \t]*htmlAyudaPortal(?=\s*<)/i, '')
    .trim()
}

export function mapPublicPortalApiToHospitalConfig(api) {
  if (!api || typeof api !== 'object') {
    return { ...DEFAULT_HOSPITAL_CONFIG }
  }

  const htmlLogin = api.htmlAyudaLogin
  const htmlRegistro = api.htmlAyudaRegistro
  const htmlPortal = normalizePortalHelpHtml(api.htmlAyudaPortal)
  const htmlTerminos = api.htmlAceptaTerminosEnRegistro
  const acepta = api.aceptaTerminosEnRegistro
  const logoRaw = api.empresaLogoPortal
  const iconoAplicacionRaw = api.iconoAplicacion
  const nombreAplicacion = api.nombreAplicacion
  const htmlFooter = api.htmlFooter ?? ''
  const primary = api.primaryColor
  const secondary = api.secondaryColor
  const portalPacienteImplementado = api.portalPacienteImplementado === true
  const autogestionImplementada = api.autogestionImplementada === true
  const permiteSacarTurno = api.permiteSacarTurno === true
  const mjeNotaInicial = textToHelpHtml(api.mjeNotaInicial)

  const logoUrl = resolveEmpresaLogoPortal(logoRaw)
  const iconoAplicacion = resolveEmpresaLogoPortal(iconoAplicacionRaw)

  return {
    ...DEFAULT_HOSPITAL_CONFIG,
    ...(htmlLogin != null && String(htmlLogin) !== '' ? { helpTextLogin: String(htmlLogin) } : {}),
    ...(htmlRegistro != null && String(htmlRegistro) !== ''
      ? { helpTextRegister: String(htmlRegistro) }
      : {}),
    ...(mjeNotaInicial !== '' ? { helpTextAutogestion: mjeNotaInicial } : {}),
    ...(htmlPortal !== ''
      ? { helpTextPortal: htmlPortal }
      : {}),
    ...(htmlTerminos != null && String(htmlTerminos) !== ''
      ? { termsAndConditionsHtml: String(htmlTerminos) }
      : {}),
    ...(logoUrl != null ? { logoUrl } : { logoUrl: null }),
    ...(iconoAplicacion != null ? { iconoAplicacion } : { iconoAplicacion: null }),
    ...(nombreAplicacion != null && String(nombreAplicacion).trim() !== ''
      ? { nombreAplicacion: String(nombreAplicacion).trim() }
      : {}),
    ...(primary != null && String(primary).trim() !== '' ? { primaryColor: String(primary).trim() } : {}),
    ...(secondary != null && String(secondary).trim() !== ''
      ? { secondaryColor: String(secondary).trim() }
      : {}),
    htmlFooter: String(htmlFooter || ''),
    portalPacienteImplementado,
    autogestionImplementada,
    permiteSacarTurno,
    /** Si es false, no se muestra ni exige aceptación de términos en registro. null/undefined → true. */
    aceptaTerminosEnRegistro: acepta !== false,
  }
}
